<template>
	<div class="flex flex-col items-start gap-1">
		<div class="pl-1 text-sm">{{ props.label }}</div>
		<div
			class="relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-md bg-[#edeef0] px-2 py-1"
			:class="{ 'border border-red-500': props.error }"
			@click="forwardClick"
		>
			<ion-datetime-button
				v-bind="$attrs"
				ref="nativeElement"
				class="flex-1"
				:style="{
					opacity: hasValue ? 1 : 0,
					pointerEvents: hasValue ? 'auto' : 'none',
				}"
			/>
			<!-- Placeholder overlay when no value -->
			<span
				v-if="!hasValue"
				class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
			>
				{{ placeholder }}
			</span>
			<ion-icon :icon="calendar" />
		</div>
		<div v-if="props.error" class="pl-1 text-xs text-red-500">
			{{ props.error }}
		</div>
	</div>
</template>

<script setup lang="ts">
	import { IonDatetimeButton, IonIcon } from "@ionic/vue";
	import { calendar } from "ionicons/icons";
	import { ref } from "vue";

	const nativeElement = ref<InstanceType<typeof IonDatetimeButton> | null>(
		null,
	);

	/** If we click any part of the date button outside of the original ionic element, forward the click to it */
	function forwardClick(event: MouseEvent) {
		const target = event.target as HTMLElement;
		const buttonElement = nativeElement.value?.$el;

		// If we didn't click directly on the ion-datetime-button, forward the click
		if (buttonElement && !buttonElement.contains(target)) {
			const shadowButton =
				buttonElement.shadowRoot?.querySelector("button");
			if (shadowButton) {
				shadowButton.click();
				event.stopPropagation();
			}
		}
	}

	interface SgDatetimeButtonProps {
		label?: string;
		/** Error message to display below the button */
		error?: string;
		/** Placeholder text to show when no value is set */
		placeholder?: string;
		/** Whether the datetime has a value (for showing placeholder) */
		hasValue?: boolean;
	}
	const props = withDefaults(defineProps<SgDatetimeButtonProps>(), {
		label: "",
		error: "",
		placeholder: "Click to select date",
		hasValue: true,
	});

	defineOptions({
		inheritAttrs: true,
	});
</script>

<style scoped>
	ion-datetime-button::part(native) {
		padding-right: 0;
		padding-left: 0;
		width: 100%;
		text-align: left;
	}
</style>
